/**
 * Unified Similar Results Component
 * 
 * Single component for displaying similar results across all contexts:
 * - Evidence Results Modal (sliding)
 * - Casefile Spots Tab
 * - SingleOrderResults Modal
 * - Any other similar results display
 * 
 * Features:
 * - Consistent UI/UX across all contexts
 * - Flexible layout modes (grid, list, compact)
 * - Context-aware styling
 * - Unified loading and error states
 * - Consistent filtering and sorting
 */

import React, { useState, useMemo } from 'react';
import { FaSync, FaFilter, FaExpand, FaCompress } from 'react-icons/fa';
import SimilarJobCard from './cards/SimilarJobCard';
import './UnifiedSimilarResults.css';

const UnifiedSimilarResults = ({
  // Data
  results = [],
  loading = false,
  error = null,
  
  // Display configuration
  viewMode = 'grid', // 'grid', 'list', 'compact'
  context = 'default', // 'evidence', 'spots', 'modal', 'panel'
  
  // Interaction handlers
  onResultSelect,
  onLoadMore,
  onRefresh,
  
  // Layout options
  showHeader = true,
  showFilters = true,
  showStats = true,
  collapsible = false,
  defaultExpanded = true,
  
  // Customization
  title = 'Similar Results',
  emptyMessage = 'No similar results found',
  className = '',
  currentUser = null // For privacy checks
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortBy, setSortBy] = useState('relevance');
  const [expandedCardId, setExpandedCardId] = useState(null); // Track which card is expanded

  // Filter options based on context
  const filterOptions = useMemo(() => {
    const baseFilters = [
      { value: 'all', label: 'All Results', count: results.length },
      { value: 'high_confidence', label: 'High Confidence', count: results.filter(r => r.confidence === 'high').length },
      { value: 'exact_matches', label: 'Exact Matches', count: results.filter(r => r.matchTypes?.includes('exact')).length }
    ];

    // Add context-specific filters
    if (context === 'evidence' || context === 'spots') {
      baseFilters.push(
        { value: 'same_evidence', label: 'Same Evidence', count: results.filter(r => r.matchTypes?.includes('same_evidence')).length },
        { value: 'vehicles', label: 'Vehicle Matches', count: results.filter(r => r.matchTypes?.includes('vehicle')).length },
        { value: 'plates', label: 'License Plates', count: results.filter(r => r.matchTypes?.includes('plate')).length }
      );
    }

    return baseFilters.filter(f => f.count > 0);
  }, [results, context]);

  // Apply filters and sorting
  const filteredAndSortedResults = useMemo(() => {
    let filtered = results;

    // Apply filters
    switch (activeFilter) {
      case 'high_confidence':
        filtered = results.filter(r => r.confidence === 'high');
        break;
      case 'exact_matches':
        filtered = results.filter(r => r.matchTypes?.some(type => type.includes('exact')));
        break;
      case 'same_evidence':
        filtered = results.filter(r => r.matchTypes?.includes('same_evidence'));
        break;
      case 'vehicles':
        filtered = results.filter(r => r.matchTypes?.some(type => type.includes('vehicle')));
        break;
      case 'plates':
        filtered = results.filter(r => r.matchTypes?.some(type => type.includes('plate')));
        break;
      default:
        filtered = results;
    }

    // Apply sorting
    switch (sortBy) {
      case 'confidence':
        filtered.sort((a, b) => {
          const confidenceOrder = { high: 3, medium: 2, low: 1 };
          return (confidenceOrder[b.confidence] || 0) - (confidenceOrder[a.confidence] || 0);
        });
        break;
      case 'similarity':
        filtered.sort((a, b) => (b.similarityScore || 0) - (a.similarityScore || 0));
        break;
      case 'date':
        filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        break;
      default: // relevance
        filtered.sort((a, b) => (b.similarityScore || 0) - (a.similarityScore || 0));
    }

    return filtered;
  }, [results, activeFilter, sortBy]);

  // Loading state
  if (loading && results.length === 0) {
    return (
      <div className={`unified-similar-results loading ${context} ${className}`}>
        {showHeader && (
          <div className="results-header">
            <h3>{title}</h3>
          </div>
        )}
        <div className="loading-state">
          <FaSync className="spinning" />
          <p>Finding similar results...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`unified-similar-results error ${context} ${className}`}>
        {showHeader && (
          <div className="results-header">
            <h3>{title}</h3>
          </div>
        )}
        <div className="error-state">
          <p>Error loading similar results: {error}</p>
          {onRefresh && (
            <button onClick={onRefresh} className="retry-button">
              <FaSync /> Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  // Empty state
  if (results.length === 0) {
    return (
      <div className={`unified-similar-results empty ${context} ${className}`}>
        {showHeader && (
          <div className="results-header">
            <h3>{title}</h3>
          </div>
        )}
        <div className="empty-state">
          <p>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div className={`unified-similar-results ${context} ${className} ${collapsible && !isExpanded ? 'collapsed' : ''}`}>
      {/* Header */}
      {showHeader && (
        <div className="results-header">
          <div className="header-left">
            <h3>{title}</h3>
            {showStats && (
              <span className="results-count">
                {filteredAndSortedResults.length} of {results.length}
              </span>
            )}
          </div>
          
          <div className="header-actions">
            {/* Filters */}
            {showFilters && filterOptions.length > 1 && (
              <div className="filter-controls">
                <FaFilter className="filter-icon" />
                <select 
                  value={activeFilter} 
                  onChange={(e) => setActiveFilter(e.target.value)}
                  className="filter-select"
                >
                  {filterOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label} ({option.count})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Sort */}
            <div className="sort-controls">
              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="relevance">Relevance</option>
                <option value="similarity">Similarity</option>
                <option value="confidence">Confidence</option>
                <option value="date">Date</option>
              </select>
            </div>

            {/* Collapse/Expand */}
            {collapsible && (
              <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="expand-button"
                title={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? <FaCompress /> : <FaExpand />}
              </button>
            )}

            {/* Refresh */}
            {onRefresh && (
              <button onClick={onRefresh} className="refresh-button" title="Refresh">
                <FaSync className={loading ? 'spinning' : ''} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Results */}
      {(!collapsible || isExpanded) && (
        <div className={`results-container ${viewMode}`}>
          <div className={`results-grid ${viewMode}`}>
            {filteredAndSortedResults.map(result => {
              const cardId = result.jobId || result.id;
              const isCardCollapsed = expandedCardId !== cardId;
              
              return (
                <SimilarJobCard
                  key={cardId}
                  similarJob={result}
                  onJobSelect={onResultSelect}
                  size={viewMode === 'compact' ? 'compact' : viewMode === 'list' ? 'compact' : 'regular'}
                  context={context}
                  currentUser={currentUser}
                  isCollapsed={isCardCollapsed}
                  onCollapseChange={(newCollapsed) => {
                    // Accordion behavior: only one card expanded at a time
                    if (!newCollapsed) {
                      // Card is being expanded, set it as the active one
                      setExpandedCardId(cardId);
                    } else {
                      // Card is being collapsed, clear the active card
                      if (expandedCardId === cardId) {
                        setExpandedCardId(null);
                      }
                    }
                  }}
                  onVideoRequest={(job, action) => {
                    console.log('🎥 Video requested for job:', job.jobId, action);
                    // Parent component can handle video modal/purchase
                  }}
                  onResultsRequest={(job, action) => {
                    console.log('🔍 Results requested for job:', job.jobId, action);
                    // Parent component can handle results modal/purchase
                    if (action !== 'purchase' && onResultSelect) {
                      onResultSelect(job);
                    }
                  }}
                />
              );
            })}
          </div>

          {/* Load More */}
          {onLoadMore && (
            <div className="load-more-container">
              <button onClick={onLoadMore} className="load-more-button">
                Load More Results
              </button>
            </div>
          )}

          {/* Loading More */}
          {loading && results.length > 0 && (
            <div className="loading-more">
              <FaSync className="spinning" />
              <span>Loading more results...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UnifiedSimilarResults;
